import React, { useState, useEffect, useRef } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Upload, Eye, LinkSimple, CheckCircle, EnvelopeSimple,
  Clock, PencilSimple, File, ArrowClockwise
} from '@phosphor-icons/react';
import DiaporamaUploadSection from './DiaporamaUploadSection';

const STATUS_LABELS = {
  stored: { label: 'STL stocke', color: 'bg-zinc-100 text-zinc-700' },
  ready_to_send: { label: 'Pret a envoyer', color: 'bg-blue-100 text-blue-700' },
  sent: { label: 'Envoye', color: 'bg-violet-100 text-violet-700' },
  viewed: { label: 'Consulte', color: 'bg-indigo-100 text-indigo-700' },
  validated: { label: 'Valide', color: 'bg-emerald-100 text-emerald-700' },
  modifications_requested: { label: 'Modification demandee', color: 'bg-amber-100 text-amber-700' },
  expired: { label: 'Expire', color: 'bg-rose-100 text-rose-700' },
};

export default function Validation3DSection({ orderId, order }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadSide, setUploadSide] = useState('left');
  const [uploading, setUploading] = useState(false);
  const [validationEmail, setValidationEmail] = useState(order?.validation_email || order?.client_email || '');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Keep email in sync when order prop changes (e.g. tab switch in details dialog)
  useEffect(() => {
    setValidationEmail(order?.validation_email || order?.client_email || '');
  }, [order?.validation_email, order?.client_email]);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderId}/validation3d`);
      setFiles(res.data);
    } catch (e) { console.error('Validation3D fetch error:', e.message); }
    setLoading(false);
  };

  useEffect(() => { fetchFiles(); }, [orderId]); // eslint-disable-line

  const handleSendCustomEmail = async () => {
    const email = (validationEmail || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/orders/${orderId}/validation3d/send`, {
        validation_email: email,
      });
      toast.success(`Lien de validation envoyé à ${email}`);
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSendingEmail(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.stl')) {
      toast.error('Veuillez sélectionner un fichier .stl');
      return;
    }
    setUploading(true);
    try {
      const maxVersion = files.filter(f => f.side === uploadSide).reduce((m, f) => Math.max(m, f.version || 1), 0);
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/orders/${orderId}/validation3d/upload?side=${uploadSide}&version=${maxVersion + 1}`, fd);
      toast.success('STL de validation uploade');
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpload = (e) => uploadFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    uploadFile(e.dataTransfer.files?.[0]);
  };

  const handleActivate = async (fileId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/validation3d/${fileId}/activate`);
      toast.success('Visualisation activee — email envoye au client');
      fetchFiles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const statusBadge = (status) => {
    const s = STATUS_LABELS[status] || { label: status, color: 'bg-zinc-100 text-zinc-700' };
    return <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-sm ${s.color}`}>{s.label}</span>;
  };

  if (loading) return <div className="text-xs text-zinc-400">Chargement...</div>;

  // Sort: newest first
  const sorted = [...files].sort((a, b) => (b.version || 0) - (a.version || 0));

  // Latest file per side (for "Visualiser les STL" global button)
  const latestLeft = sorted.find(f => f.side === 'left' && f.token);
  const latestRight = sorted.find(f => f.side === 'right' && f.token);

  return (
    <div data-testid="validation3d-section">
      <p className="label-overline mb-3 flex items-center gap-2">
        <Eye size={16} />Validation 3D client
      </p>

      {/* Email cible pour la validation */}
      <div className="mb-3 bg-blue-50 border border-blue-200 rounded-sm p-3" data-testid="v3d-email-block">
        <Label className="text-[11px] font-semibold text-blue-900 uppercase tracking-wider flex items-center gap-1.5">
          <EnvelopeSimple size={12} />Email de validation
        </Label>
        <p className="text-[11px] text-blue-700 mt-0.5 mb-2">
          Adresse à laquelle sera envoyé le lien de visualisation 3D (l'audioprothésiste peut valider depuis son portable).
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            value={validationEmail}
            onChange={(e) => setValidationEmail(e.target.value)}
            placeholder="email@audioprothesiste.fr"
            className="rounded-sm bg-white text-xs flex-1"
            data-testid="v3d-validation-email-input"
          />
          <Button
            size="sm"
            onClick={handleSendCustomEmail}
            disabled={sendingEmail || !validationEmail.trim()}
            className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white text-xs h-9"
            data-testid="v3d-send-email-btn">
            <EnvelopeSimple size={12} className="mr-1" />
            {sendingEmail ? 'Envoi...' : (order?.validation_email ? 'Renvoyer le lien' : 'Envoyer le lien')}
          </Button>
        </div>
        {order?.validation_email && order?.validation_email !== order?.client_email && (
          <p className="text-[10px] text-blue-600 mt-1.5 italic">
            ↳ Adresse personnalisée (différente de l'email client par défaut : {order?.client_email}).
          </p>
        )}
      </div>

      {/* Upload — drag & drop ou clic */}
      <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-3">
        <Select value={uploadSide} onValueChange={setUploadSide}>
          <SelectTrigger className="rounded-sm bg-white w-full sm:w-36 text-xs" data-testid="v3d-upload-side">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="left">Gauche (L)</SelectItem>
            <SelectItem value="right">Droit (R)</SelectItem>
          </SelectContent>
        </Select>
        <div
          className={`flex-1 border-2 border-dashed rounded-sm px-3 py-4 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-blue-500 bg-blue-50' : 'border-zinc-300 bg-white hover:border-zinc-400 hover:bg-zinc-50'
          } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
          data-testid="v3d-dropzone"
        >
          <input ref={fileInputRef} type="file" accept=".stl" className="hidden" onChange={handleUpload} data-testid="v3d-file-input" />
          <Upload size={20} className={`mx-auto mb-1 ${dragOver ? 'text-blue-500' : 'text-zinc-400'}`} />
          <p className="text-xs text-zinc-600 font-medium">
            {uploading ? 'Upload en cours...' : `Glissez le fichier STL ici ou cliquez (côté ${uploadSide === 'left' ? 'Gauche' : 'Droit'})`}
          </p>
        </div>
      </div>

      {/* Global "Visualiser les STL" button — opens latest version */}
      {(latestLeft || latestRight) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {latestRight && (
            <a href={`${window.location.origin}/viewer/${latestRight.token}`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-8" data-testid="v3d-view-latest-right">
                <Eye size={14} className="mr-1.5" />Visualiser STL Droit (V{latestRight.version})
              </Button>
            </a>
          )}
          {latestLeft && (
            <a href={`${window.location.origin}/viewer/${latestLeft.token}`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-8" data-testid="v3d-view-latest-left">
                <Eye size={14} className="mr-1.5" />Visualiser STL Gauche (V{latestLeft.version})
              </Button>
            </a>
          )}
        </div>
      )}

      {/* Files list — versioned, newest first */}
      {sorted.length === 0 ? (
        <p className="text-xs text-zinc-400 italic">Aucun fichier de validation 3D</p>
      ) : (
        <div className="space-y-2">
          {sorted.map((f, idx) => (
            <div key={f.id} className={`border p-3 rounded-sm ${idx === 0 || (sorted[idx - 1]?.side !== f.side && sorted.filter(x => x.side === f.side)[0]?.id === f.id) ? 'bg-white border-zinc-300' : 'bg-zinc-50 border-zinc-200'}`} data-testid={`v3d-file-${f.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <File size={14} className="text-zinc-400 flex-shrink-0" />
                  <span className="text-xs font-medium truncate">{f.original_filename}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${
                    sorted.filter(x => x.side === f.side)[0]?.id === f.id ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-200 text-zinc-600'
                  }`}>V{f.version}</span>
                  <span className="text-[10px] text-zinc-400">{f.side === 'left' ? 'OG' : 'OD'}</span>
                </div>
                {statusBadge(f.status)}
              </div>

              {/* Meta */}
              <div className="flex items-center gap-3 text-[10px] text-zinc-400 mb-2">
                {f.created_at && <span>{new Date(f.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>}
                {f.created_by && <span>{f.created_by}</span>}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-1.5">
                {/* Activate visualization */}
                {(f.status === 'stored' || f.status === 'ready_to_send' || f.status === 'modifications_requested') && (
                  <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm"
                    onClick={() => handleActivate(f.id)} data-testid={`v3d-activate-${f.id}`}>
                    <LinkSimple size={12} className="mr-1" />Activer et envoyer au client
                  </Button>
                )}

                {/* View THIS specific version */}
                {f.token && !['expired'].includes(f.status) && (
                  <a href={`${window.location.origin}/viewer/${f.token}`} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="h-7 text-[10px] rounded-sm" data-testid={`v3d-view-${f.id}`}>
                      <Eye size={12} className="mr-1" />Visualiser V{f.version}
                    </Button>
                  </a>
                )}

                {f.status === 'validated' && (
                  <span className="flex items-center gap-1 text-[10px] text-emerald-600">
                    <CheckCircle size={12} weight="fill" />
                    Valide le {new Date(f.validated_at).toLocaleString('fr-FR')}
                  </span>
                )}

                {f.status === 'modifications_requested' && f.client_comment && (
                  <div className="w-full mt-1 bg-amber-50 border border-amber-200 p-2 rounded-sm">
                    <p className="text-[10px] font-medium text-amber-700">Commentaire client :</p>
                    <p className="text-xs text-amber-800">{f.client_comment}</p>
                  </div>
                )}
              </div>

              {/* Mini history */}
              {f.action_history && f.action_history.length > 0 && (
                <details className="mt-2">
                  <summary className="text-[10px] text-zinc-400 cursor-pointer hover:text-zinc-600">
                    Historique ({f.action_history.length})
                  </summary>
                  <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                    {[...f.action_history].reverse().map((a) => (
                      <div key={`${a.timestamp}-${a.action}`} className="text-[10px] text-zinc-500 flex gap-2">
                        <span className="text-zinc-400 flex-shrink-0">{new Date(a.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        <span>{a.action}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Section Diaporama Photos design — accessible côté client via Viewer3D */}
      <DiaporamaUploadSection orderId={orderId} />
    </div>
  );
}
